/**
 * Orders API Service (client-side)
 *
 * SOC 2 (C9): customer-facing by-GUID / by-cartId order lookups previously ran
 * a Firestore `where('guid'|'cartId', ...)` LIST query against the `orders`
 * collection, which required a blanket `allow list` rule that let any
 * authenticated (incl. anonymous) session ENUMERATE every restaurant's orders.
 * Those reads now go through the authenticated API, which resolves a SINGLE
 * record server-side via the admin SDK (the unguessable GUID is the
 * capability). The Firestore `orders` list rule is now staff-only.
 */

import { Order } from "@order-up/models";
import { createApiClient, getApi } from "./apiClient";

const apiClient = createApiClient("/api/v1/orders");
// Public/customer app (checkout, ordering): authenticate with the Firebase
// Bearer token (incl. the anonymous session), NOT a cross-domain session
// cookie — mirrors checkout.ts. Otherwise the browser attempts a credentialed
// cross-origin request that CORS rejects and that carries the wrong identity.
apiClient.defaults.withCredentials = false;

/**
 * Resolve a single order by its unguessable GUID, scoped to one
 * tenant/restaurant (numeric business IDs).
 * @returns the Order model, or null if not found
 */
const getOrderByGuid = async (
  orderGuid: string,
  tenantId: number,
  locationNumericId: number,
  // Durable order-access token (from the checkout response / receipt link). When
  // present it authorizes the read across devices / after the anon session is
  // gone; otherwise the API falls back to the owner/staff gate.
  accessToken?: string,
): Promise<Order | null> => {
  try {
    const data = await getApi<Record<string, unknown> | null>(
      apiClient,
      `/by-guid/${encodeURIComponent(orderGuid)}`,
      { params: { tenantId, restaurantId: locationNumericId, ...(accessToken ? { accessToken } : {}) } },
    );
    if (!data) return null;
    return Order.fromJSON(data);
  } catch (error: any) {
    if (error?.status === 404 || error?.message?.includes("404")) {
      return null;
    }
    throw error;
  }
};

/**
 * PCI-safe, customer-facing payment summary for one order — the shape returned
 * by `GET /api/v1/orders/by-guid/:guid/payments`. Display fields ONLY: no PAN /
 * maskedPan, cardholder name, processor refs, tokens, or metadata. Mirrors the
 * server's `toCustomerPaymentSummary` projection.
 */
export interface CustomerPaymentSummary {
  /** Charged total (base + tip); negative on a refund child. */
  amount: number;
  /** Check-applied portion (amount − tipAmount). */
  baseAmount: number | null;
  tipAmount: number;
  taxAmount: number | null;
  paymentMethod: string;
  cardBrand: string | null;
  cardLast4: string | null;
  entryMethod: string | null;
  status: string;
  /** ISO timestamp the payment processed. */
  processedAt: string | null;
  originalPaymentId: number | null;
  originalTransactionRef: string | null;
  /** True for a refund child (refund linkage present or negative amount). */
  isRefund: boolean;
}

/**
 * Fetch the PCI-safe payment summary for an order via the token-gated
 * customer route. Same capability model as {@link getOrderByGuid}: the durable
 * order-access token (or order owner / staff) authorizes the read across
 * devices / after the anon session is gone.
 */
const getOrderPaymentsByToken = async (
  orderGuid: string,
  tenantId: number,
  locationNumericId: number,
  accessToken?: string,
): Promise<CustomerPaymentSummary[]> => {
  const data = await getApi<{ payments?: CustomerPaymentSummary[] } | null>(
    apiClient,
    `/by-guid/${encodeURIComponent(orderGuid)}/payments`,
    { params: { tenantId, restaurantId: locationNumericId, ...(accessToken ? { accessToken } : {}) } },
  );
  return data?.payments ?? [];
};

/**
 * Resolve a single order by the cart it was created from (duplicate-payment
 * guard on checkout retry). A `null` body (HTTP 200) means "no order yet".
 */
const getOrderByCartId = async (
  cartId: number,
  tenantId: number,
  locationNumericId: number,
): Promise<Order | null> => {
  try {
    const data = await getApi<Record<string, unknown> | null>(
      apiClient,
      `/by-cart/${cartId}`,
      { params: { tenantId, restaurantId: locationNumericId } },
    );
    // "No order yet" can arrive as null OR as a truthy-but-empty/partial body
    // (e.g. `{}`). Treat anything without a numeric orderId as "no order"
    // rather than letting Order.fromJSON throw — this is a pre-checkout
    // duplicate-order probe, and a missing order is the normal case.
    if (!data || typeof data.orderId !== 'number') return null;
    return Order.fromJSON(data);
  } catch (error: any) {
    if (error?.status === 404 || error?.message?.includes("404")) {
      return null;
    }
    throw error;
  }
};

export const ordersApi = {
  getOrderByGuid,
  getOrderByCartId,
  getOrderPaymentsByToken,
};
